// ABOUTME: Collapsible sandbox of the define step: the user types a sample text and sees its replacement live.
// ABOUTME: Uses the browser's String.prototype.replace with the same pattern the scan will use, plus the diff view.

import { Box, Button, Paper, Stack, TextField, Typography } from '@ds/coolshop';
import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { computeDiffSegments, FULL_DIFF_CONTEXT } from '../utils/computeDiffSegments';
import { DiffSegmentsView } from './DiffCell';

type TryItBoxProps = {
    /** The compiled find pattern; `null` while it is empty or invalid. */
    regex: RegExp | null;
    replace: string;
};

export function TryItBox({ regex, replace }: TryItBoxProps) {
    const { formatMessage } = useIntl();
    const [open, setOpen] = useState(false);
    const [sample, setSample] = useState('');

    const preview = useMemo(() => {
        if (!regex || !sample) return null;
        const matchCount = Array.from(sample.matchAll(regex)).length;
        return {
            matchCount,
            result: sample.replace(regex, replace),
            diff: computeDiffSegments(sample, regex, replace, FULL_DIFF_CONTEXT),
        };
    }, [regex, sample, replace]);

    return (
        <Stack gap={1}>
            <Box>
                <Button variant="text" size="small" onClick={() => setOpen(!open)}>
                    {open
                        ? formatMessage({
                              id: 'findReplace.tryIt.hide',
                              defaultMessage: 'Hide the test box',
                          })
                        : formatMessage({
                              id: 'findReplace.tryIt.show',
                              defaultMessage: 'Try it on a sample text',
                          })}
                </Button>
            </Box>
            {open && (
                <Paper elevation={0} bgcolor="background.secondary">
                    <Stack gap={2} p={2}>
                        <TextField
                            label={formatMessage({
                                id: 'findReplace.tryIt.sample',
                                defaultMessage: 'Sample text',
                            })}
                            value={sample}
                            onChange={e => setSample(e.target.value)}
                            multiline
                            minRows={2}
                            fullWidth
                        />
                        {!regex && (
                            <Typography variant="body2" color="textSecondary">
                                {formatMessage({
                                    id: 'findReplace.tryIt.noPattern',
                                    defaultMessage: 'Enter a valid find pattern to test it.',
                                })}
                            </Typography>
                        )}
                        {regex && !sample && (
                            <Typography variant="body2" color="textSecondary">
                                {formatMessage({
                                    id: 'findReplace.tryIt.noSample',
                                    defaultMessage:
                                        'Type a sample text above to see what the replacement does to it.',
                                })}
                            </Typography>
                        )}
                        {preview && (
                            <Stack gap={1} data-testid="find-replace-try-it-result">
                                <Typography variant="body2" color="textSecondary">
                                    {formatMessage(
                                        {
                                            id: 'findReplace.tryIt.matches',
                                            defaultMessage:
                                                '{count, plural, =0 {No match} one {# match} other {# matches}}',
                                        },
                                        { count: preview.matchCount },
                                    )}
                                </Typography>
                                <DiffSegmentsView segments={preview.diff.segments} />
                                <Typography variant="caption" color="textSecondary">
                                    {formatMessage({
                                        id: 'findReplace.tryIt.result',
                                        defaultMessage: 'Result',
                                    })}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    fontFamily="monospace"
                                    whiteSpace="pre-wrap"
                                    data-testid="find-replace-try-it-output"
                                >
                                    {preview.result}
                                </Typography>
                            </Stack>
                        )}
                    </Stack>
                </Paper>
            )}
        </Stack>
    );
}
